/**
 * VirtualAccountsListProvider (TMMAPP-0004). Mounted by `AppShell`: loads `GET /v1/virtual-accounts` once
 * per shell mount (the `MeProvider` pattern), exposes `reload()`, and owns the Create dialog so
 * the page's New virtual account button and the allocate dialogs open the same one via `openCreate()`.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { virtualAccountsApi, type VirtualAccountListItem } from '@/api/virtualAccounts';
import { isApiError, type ApiError } from '@/api/errors';
import { useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { useMeRefetch } from '@/session/meHooks';
import { CreateVirtualAccountDialog } from './CreateVirtualAccountDialog';
import {
  VirtualAccountsListContext,
  type VirtualAccountsListStatus,
  type VirtualAccountsListValue,
} from './virtualAccountsListContext';

interface ListState {
  status: VirtualAccountsListStatus;
  items: VirtualAccountListItem[];
  error: ApiError | null;
}

export function VirtualAccountsListProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const refetchMe = useMeRefetch();
  const openSubscribe = useOpenSubscribe();
  const [state, setState] = useState<ListState>({ status: 'loading', items: [], error: null });
  const [createOpen, setCreateOpen] = useState(false);
  // StrictMode double-invokes the mount effect; the list request is issued once.
  const started = useRef(false);

  const reload = useCallback(async () => {
    try {
      // The owner's ARCHIVED virtual accounts ride along for the list's archived section (TMMAPP-0005);
      // the sidebar reads `/me`, which stays ACTIVE-only.
      const items = await virtualAccountsApi.list({ includeArchived: true });
      setState({ status: 'ready', items, error: null });
    } catch (error) {
      setState((prev) => ({
        status: 'error',
        items: prev.items,
        error: isApiError(error) ? error : null,
      }));
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void reload();
  }, [reload]);

  const openCreate = useCallback(() => setCreateOpen(true), []);

  const value = useMemo<VirtualAccountsListValue>(
    () => ({ ...state, reload, openCreate }),
    [state, reload, openCreate],
  );

  return (
    <VirtualAccountsListContext.Provider value={value}>
      {children}
      <CreateVirtualAccountDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async (detail) => {
          setCreateOpen(false);
          await Promise.all([reload(), refetchMe()]);
          void navigate(`/v/${detail.id}`);
        }}
        onSubscriptionRequired={() => {
          setCreateOpen(false);
          openSubscribe();
        }}
      />
    </VirtualAccountsListContext.Provider>
  );
}
